import React, { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
	Building2,
	Plus,
	X,
	MapPin,
	AlertCircle,
	Trash2,
	Hospital,
} from "lucide-react";
import { supabase } from "@/lib/supabase";
import { usePlacesAutocomplete } from "@/hooks/usePlacesAutocomplete";
import { DoulaExcludedProvider } from "@/types/index";
import styles from "./onboarding.module.css";

interface ExcludedProvidersStepProps {
	onNext: (data: any) => void;
	onBack: () => void;
	initialData?: any;
}

interface Provider {
	name: string;
	address: string;
	place_id: string;
	types: string[];
	formatted_address: string;
	city: string;
	state: string;
	zip: string;
	latitude: number;
	longitude: number;
}

export const ExcludedProvidersStep = ({
	onNext,
	onBack,
	initialData,
}: ExcludedProvidersStepProps) => {
	const [excludedProviders, setExcludedProviders] = useState<Provider[]>(
		initialData?.excludedProviders || [],
	);
	const [newProviderName, setNewProviderName] = useState("");
	const [loading, setLoading] = useState(false);
	const inputRef = useRef<HTMLInputElement>(null);

	const { place } = usePlacesAutocomplete({
		inputRef,
		types: ["health"],
		componentRestrictions: { country: "us" },
		fields: ["place_id", "formatted_address", "name", "types", "geometry", "address_components"],
	});

	useEffect(() => {
		if (!place?.place_id || !place.name) return;
		handleAddProvider(place);
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [place]);

	const handleAddProvider = (place: any) => {
		if (!place.place_id || !place.name) {
			console.warn("Invalid place data received");
			return;
		}

		// Check if provider already exists
		const exists = excludedProviders.some(
			(provider) => provider.place_id === place.place_id,
		);
		if (exists) {
			alert("This provider has already been added to your exclusion list.");
			setNewProviderName("");
			return;
		}

		// Parse address components
		let city = "";
		let state = "";
		let zip = "";

		if (place.address_components) {
			place.address_components.forEach((component: any) => {
				if (component.types.includes("locality")) {
					city = component.long_name;
				} else if (component.types.includes("administrative_area_level_1")) {
					state = component.short_name;
				} else if (component.types.includes("postal_code")) {
					zip = component.long_name;
				}
			});
		}

		const newProvider: Provider = {
			name: place.name,
			address: place.formatted_address || "",
			place_id: place.place_id,
			types: place.types || [],
			formatted_address: place.formatted_address || "",
			city,
			state,
			zip,
			latitude: place.geometry?.location ? place.geometry.location.lat() : 0,
			longitude: place.geometry?.location ? place.geometry.location.lng() : 0,
		};

		setExcludedProviders((prev) => [...prev, newProvider]);
		setNewProviderName("");
	};

	const handleRemoveProvider = (indexToRemove: number) => {
		setExcludedProviders((prev) =>
			prev.filter((_, index) => index !== indexToRemove),
		);
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		setLoading(true);

		try {
			// Call the edge function to save excluded providers
			const response = await fetch(
				`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/update-doula-profile`,
				{
					method: "POST",
					headers: {
						"Content-Type": "application/json",
						Authorization: `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
					},
					body: JSON.stringify({
						step: "excluded_providers",
						data: {
							excluded_providers: {
								providers: excludedProviders,
							},
						},
					}),
				},
			);

			const result = await response.json();

			if (!response.ok) {
				throw new Error(result.error || "Failed to save excluded providers");
			}

			onNext({ excludedProviders });
		} catch (error) {
			console.error("Error saving excluded providers:", error);
			alert("Failed to save excluded providers. Please try again.");
		} finally {
			setLoading(false);
		}
	};

	const getProviderTypeLabel = (types: string[]) => {
		if (!types || !Array.isArray(types)) return "Healthcare Provider";
		if (types.includes("hospital")) return "Hospital";
		if (types.includes("health")) return "Healthcare";
		if (types.includes("doctor")) return "Medical Practice";
		if (types.includes("establishment")) return "Medical Facility";
		return "Healthcare Provider";
	};

	return (
		<motion.div
			initial={{ opacity: 0, y: 20 }}
			animate={{ opacity: 1, y: 0 }}
			transition={{ duration: 0.6 }}
			className={styles.wrapper}
		>
			{/* Progress Bar */}
			<div className={styles.progressSection}>
				<div className={styles.progressHeader}>
					<span className={styles.progressLabel}>Step 6 of 15</span>
					<span className={styles.progressLabel}>Provider Exclusions</span>
				</div>
				<div className={styles.progressTrack}>
					<div className={styles.progressFill} style={{ width: "40%" }} />
				</div>
			</div>

			<Card className={styles.card}>
				<CardHeader className="text-center pb-6">
					<div className={styles.iconContainer}>
						<Building2 className={styles.iconLarge} />
					</div>
					<CardTitle className={styles.cardTitle}>
						Provider Exclusions
					</CardTitle>
					<p className={`${styles.cardSubtitle} max-w-2xl mx-auto`}>
						List any healthcare providers or hospitals you prefer not to work
						with. This is completely confidential and helps us match you with
						the right families.
					</p>
				</CardHeader>

				<CardContent className="space-y-6">
					<form onSubmit={handleSubmit} className="space-y-6">
						{/* Add Provider Section */}
						<div className="space-y-4">
							<Label htmlFor="provider-input" className={styles.fieldLabel}>
								Add Healthcare Provider or Hospital
							</Label>
							<div className="relative">
								<MapPin className={styles.inputIcon} />
								<Input
									ref={inputRef}
									id="provider-input"
									type="text"
									placeholder="Search for hospitals, medical centers, or healthcare providers..."
									value={newProviderName}
									onChange={(e) => setNewProviderName(e.target.value)}
									className="pl-10 h-12 text-lg"
								/>
							</div>
							<div className={styles.infoAlert}>
								<div className="flex items-start space-x-3">
									<AlertCircle className={styles.infoIcon} />
									<div className={styles.infoText}>
										<p className="font-medium mb-1">Privacy Note:</p>
										<p>
											Your exclusion list is completely confidential and will
											never be shared with parents or other users. This
											information is only used to filter your profile from
											appearing in search results for families using those
											providers.
										</p>
									</div>
								</div>
							</div>
						</div>

						{/* Current Excluded Providers */}
						{excludedProviders.length > 0 && (
							<div className="space-y-4">
								<Label className={styles.fieldLabel}>
									Your Excluded Providers ({excludedProviders.length})
								</Label>
								<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
									{excludedProviders.map((provider, index) => (
										<motion.div
											key={`${provider.place_id}-${index}`}
											initial={{ opacity: 0, scale: 0.95 }}
											animate={{ opacity: 1, scale: 1 }}
											transition={{ duration: 0.3 }}
										>
											<Card className="border border-newTheme-lightBlue hover:border-red-300 transition-colors bg-newTheme-white">
												<CardContent className="p-4">
													<div className="flex items-start justify-between">
														<div className="flex items-start space-x-3 flex-1 min-w-0">
															<div className="bg-red-100 rounded-lg p-2 flex-shrink-0">
																<Hospital className="h-4 w-4 text-red-600" />
															</div>
															<div className="flex-1 min-w-0">
																<h4 className="font-semibold text-newTheme-black font-karla text-sm truncate">
																	{provider.name}
																</h4>
																<p className="text-xs text-newTheme-black/60 font-karla truncate">
																	{provider.formatted_address}
																</p>
																<Badge
																	variant="secondary"
																	className={`text-xs mt-1 ${styles.badge}`}
																>
																	{getProviderTypeLabel(provider.types)}
																</Badge>
															</div>
														</div>
														<Button
															type="button"
															variant="ghost"
															size="sm"
															onClick={() => handleRemoveProvider(index)}
															className="text-red-600 hover:text-red-700 hover:bg-red-50 flex-shrink-0 ml-2"
														>
															<Trash2 className="h-4 w-4" />
														</Button>
													</div>
												</CardContent>
											</Card>
										</motion.div>
									))}
								</div>
							</div>
						)}

						{/* Empty State */}
						{excludedProviders.length === 0 && (
							<div className="text-center py-8">
								<div className={styles.iconContainer}>
									<Building2 className="h-8 w-8 text-newTheme-spruce/40" />
								</div>
								<h3 className="text-lg font-semibold text-newTheme-black font-karla mb-2">
									No Excluded Providers
								</h3>
								<p className="text-newTheme-black/60 font-karla">
									You haven't excluded any providers yet. You can add providers
									above or skip this step if you don't have any preferences.
								</p>
							</div>
						)}

						{/* Navigation Buttons */}
						<div className={styles.navRowBordered}>
							<Button
								type="button"
								variant="outline"
								onClick={onBack}
								className={styles.backButton}
							>
								Back
							</Button>
							<Button
								type="submit"
								disabled={loading}
								className={styles.submitButton}
							>
								{loading ? (
									<>
										<div className={styles.spinner} />
										Saving...
									</>
								) : (
									"Continue"
								)}
							</Button>
						</div>
					</form>
				</CardContent>
			</Card>

		</motion.div>
	);
};
